import { Observable, share } from 'rxjs';
import { type Logger } from 'loglevel';
import { Specs } from '@doxyme/extensions-common';
import { type ServiceTokens } from '../../container';
import { type AppStore } from '../../stores/redux';
import {
  type RealtimeMessage,
  RealtimeMessageType,
} from '../../features/realtime/realtime.types';
import { type CommonRealtimeService } from '../call/realtime/commonRealtime.service';
import { api } from './operations.generated';

type CompletedPayload = Specs.Transcription.CompletedPayload;
type TranscriptionOutput = Specs.Transcription.TranscriptionOutput;

type TranscriptionServiceDependencies = {
  [ServiceTokens.ReduxStore]: AppStore;
  [ServiceTokens.CommonRealtime]: CommonRealtimeService;
  [ServiceTokens.Logger]: Logger;
};

export type TranscriptionStatusPayload = {
  transcriptionId: string;
  isSuccess: boolean;
  errorMessage?: string;
  data?: {
    url: string;
    serviceUuid: string;
    audioUrl?: string;
  };
};

export class TranscriptionService {
  readonly transcriptionStatus: Observable<TranscriptionStatusPayload>;

  private readonly redux: AppStore;

  private readonly realtime: CommonRealtimeService;

  protected readonly logger: Logger;

  constructor({
    reduxStore,
    commonRealtime,
    logger,
  }: TranscriptionServiceDependencies) {
    this.redux = reduxStore;
    this.realtime = commonRealtime;
    this.logger = logger;

    this.transcriptionStatus = new Observable<TranscriptionStatusPayload>(
      (observer) => {
        const unsubscribe = this.realtime.on(
          RealtimeMessageType.TranscriptionStatus,
          async (message: RealtimeMessage<TranscriptionStatusPayload>) => {
            if (message.payload) {
              observer.next(message.payload);
            }
          },
        );

        return () => {
          unsubscribe();
        };
      },
    ).pipe(share());
  }

  /**
   * Start archiving
   * @param sessionId - vonage session id
   * @param output - optional output types to request from the archive (e.g. transcript, audio)
   */
  async start(
    sessionId: string,
    output?: TranscriptionOutput[],
  ): Promise<string> {
    const response = await this.redux.dispatch(
      api.endpoints.startArchive.initiate({
        input: {
          sessionId,
          transcriptRequested: output?.includes('transcript') ?? true,
          audioRequested: output?.includes('audio') ?? false,
        },
      }),
    );

    if ('error' in response) {
      const message = 'Archive start failed.';
      this.logger.error(message, {
        action: 'start',
        sessionId,
        error: response.error,
      });
      throw new Error(message);
    }

    const transcription = response.data.Transcription.startArchive;
    return transcription.id;
  }

  /**
   * Stop archiving
   * @param id - transcription entity id
   */
  async stop(id: string): Promise<void> {
    const response = await this.redux.dispatch(
      api.endpoints.stopArchive.initiate({
        input: { id },
      }),
    );

    if ('error' in response) {
      const message = 'Archive stop failed';
      this.logger.error(message, {
        action: 'stop',
        transcriptionId: id,
        error: response.error,
      });
      throw new Error(message);
    }
  }

  /**
   * Cancel archiving
   * @param id - transcription entity id
   */
  async cancel(id: string): Promise<void> {
    const response = await this.redux.dispatch(
      api.endpoints.cancelArchive.initiate({
        input: { id },
      }),
    );

    if ('error' in response) {
      const message = 'Archive cancel failed.';
      this.logger.error(message, {
        action: 'cancel',
        transcriptionId: id,
        error: response.error,
      });
      throw new Error(message);
    }
  }

  async delete(id: string): Promise<void> {
    const response = await this.redux.dispatch(
      api.endpoints.deleteTranscript.initiate({
        input: { id },
      }),
    );

    if ('error' in response) {
      const message = 'Transcript delete failed.';
      this.logger.error(message, {
        action: 'delete',
        transcriptionId: id,
        error: response.error,
      });
      throw new Error(message);
    }
  }

  async downloadTranscription(url: string): Promise<CompletedPayload> {
    const response = await fetch(url);

    if (!response.ok) {
      this.logger.error('Transcript download failed.', {
        action: 'downloadTranscription',
        statusText: response.statusText,
        status: response.status,
      });
      throw new Error('Transcription download failed.');
    }

    const payload = await response.json();
    return { ...payload, text: payload.text ?? '' };
  }
}
